import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { IssueCard } from '../../components/IssueCard'
import { DrillDownRow } from '../../components/DrillDownRow'
import { DrillDownSection } from '../../components/DrillDownSection'
import { ContentHeader } from '../../components/layout/ContentHeader'
import { useApi, asArray } from '../../hooks/useApi'

type Severity = 'critical' | 'warning' | 'info'
type Filter = 'all' | Severity

interface Issue { id: number; type: string; severity: Severity; title: string; description: string; recommendation?: string; count?: number }
interface AuditData { id: number; status: string; scoreTech?: number }
interface RawSummary { total?: number; status200?: number; status301?: number; status302?: number; status404?: number; status5xx?: number; missingTitle?: number; missingMeta?: number; missingH1?: number; missingCanonical?: number; thinContent?: number; noSchema?: number; noOg?: number }

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Tout' },
  { value: 'critical', label: 'Critique' },
  { value: 'warning', label: 'Attention' },
  { value: 'info', label: 'Info' },
]

export default function AuditErrors() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('all')

  const { data: audits } = useApi<AuditData[]>(`/api/clients/${projectId}/audits`)
  const auditList = Array.isArray(audits) ? audits : []; const latestAudit = auditList.find(a => a.status === 'completed')
  const auditId = latestAudit?.id ? String(latestAudit.id) : null

  const { data: issues, loading: loadingIssues } = useApi<Issue[]>(
    auditId ? `/api/reports/${auditId}/issues?section=technical` : '/api/health'
  )
  const { data: rawSummary } = useApi<RawSummary>(
    auditId ? `/api/audits/${auditId}/summary` : '/api/health'
  )
  const summary = rawSummary ? {
    total: rawSummary.total ?? 0, status200: rawSummary.status200 ?? 0, status301: rawSummary.status301 ?? 0,
    status302: rawSummary.status302 ?? 0, status404: rawSummary.status404 ?? 0, status5xx: rawSummary.status5xx ?? 0,
    missingTitle: rawSummary.missingTitle ?? 0, missingMeta: rawSummary.missingMeta ?? 0, missingCanonical: rawSummary.missingCanonical ?? 0,
  } : null

  const DRILLDOWN_TYPES = new Set(['missing_title', 'missing_meta_desc', 'missing_canonical', 'page_404', 'server_error', 'redirect_302'])
  const issueList = asArray(issues)
  const grouped = issueList
    .filter(i => !DRILLDOWN_TYPES.has(i.type))
    .reduce<Record<string, Issue>>((acc, i) => {
      if (!acc[i.type]) {
        acc[i.type] = { ...i, count: 1 }
      } else {
        acc[i.type].count = (acc[i.type].count ?? 0) + 1
      }
      return acc
    }, {})
  const deduplicated = Object.values(grouped)
  const filtered = filter === 'all' ? deduplicated : deduplicated.filter(i => i.severity === filter)

  return (
    <>
      <ContentHeader
        breadcrumb={[{ label: 'Audit de site Web', to: `/p/${projectId}/audit` }, { label: "Rapport d'erreurs" }]}
        title="Rapport d'erreurs"
        subtitle={latestAudit ? `Score technique : ${latestAudit.scoreTech ?? '-'}/100` : undefined}
        filters={
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button key={f.value} onClick={() => setFilter(f.value)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-orion-primary text-[#1B2559]' : 'bg-[#F4F5FA] text-[#8B8FA7] hover:text-[#1B2559]'}`}>
                {f.label}
                {f.value !== 'all' && deduplicated.length > 0 && <span className="ml-1.5 font-mono text-xs opacity-70">{deduplicated.filter(i => i.severity === f.value).length}</span>}
              </button>
            ))}
          </div>
        }
      />

      {auditId && summary && (
        <div className="space-y-4 mb-6">
          <DrillDownSection title="Codes HTTP" subtitle={`${summary.total.toLocaleString()} pages analysées`}>
            <DrillDownRow label="200 OK" count={summary.status200} total={summary.total} filter="status200" auditId={auditId} severity="info" />
            <DrillDownRow label="301 Redirect" count={summary.status301} total={summary.total} filter="status301" auditId={auditId} severity="warning" />
            <DrillDownRow label="302 Redirect" count={summary.status302} total={summary.total} filter="status302" auditId={auditId} severity="warning" />
            <DrillDownRow label="404 Not Found" count={summary.status404} total={summary.total} filter="status404" auditId={auditId} severity="critical" />
            <DrillDownRow label="5xx Erreur serveur" count={summary.status5xx} total={summary.total} filter="status5xx" auditId={auditId} severity="critical" />
          </DrillDownSection>
          <DrillDownSection title="Balises manquantes">
            <DrillDownRow label="Title manquant" count={summary.missingTitle} total={summary.total} filter="missingTitle" auditId={auditId} severity="critical" />
            <DrillDownRow label="Meta description manquante" count={summary.missingMeta} total={summary.total} filter="missingMeta" auditId={auditId} severity="warning" />
            <DrillDownRow label="Canonical manquant" count={summary.missingCanonical} total={summary.total} filter="missingCanonical" auditId={auditId} severity="warning" />
          </DrillDownSection>
        </div>
      )}

      {(!loadingIssues && filtered.length > 0 || loadingIssues) && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-[#1B2559]">Autres probl&egrave;mes</h3>
          {loadingIssues && [...Array(4)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E2E4ED] rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-[#F4F5FA] rounded w-3/4" />
              <div className="h-3 bg-[#F4F5FA] rounded w-1/2 mt-2" />
            </div>
          ))}
          {!loadingIssues && filtered.map(issue => (
            <IssueCard key={issue.id} type={issue.type} severity={issue.severity} title={issue.title} description={issue.description} recommendation={issue.recommendation} count={issue.count} onClick={() => navigate(`/p/${projectId}/audit/issues/${issue.type}?auditId=${auditId}`)} />
          ))}
        </div>
      )}
    </>
  )
}
